import { DiscoverOfferingType, DiscoverPrefix, EventType, VenueOfferingType } from './App.types'

export enum Routes {
  DISCOVER_CITY = '/b',
  APP_DISCOVER_SPORT_CITY = '/a/l',
  HOME = '/',
  ACTIVITY_DETAILS = '/activity',
}

export enum UserRoutes {
  MY_BOOKINGS = '/u',
  MY_CORPORATE_BENEFITS = '/u/corporate-benefits',
  MY_FAMILY = '/u/family',
  MY_FAVORITES = '/u/favorites',
  MY_PAYMENT_METHODS = '/u/payment-methods',
  MY_PERSONAL_INFORMATION = '/u/edit',
  MY_PRODUCTS = '/u/products',
  MY_REFER_A_FRIEND = '/u/refer-a-friend',
  MY_VIDEOS = '/u/videos',
  MY_PRIVACY_SETTINGS = '/u/privacy',
}

export enum VenueProfileTabsPrefix {
  OVERVIEW = '/s/',
  SPORTS_FIELDS = '/sb/',
  CLASSES = '/scl/',
  COURSES = '/sk/',
  CAMPS = '/sc/',
  EVENTS = '/sv/',
  EDUCATION = '/se/',
  WORKSHOPS = '/sw/',
  RETREATS = '/srt/',
  TRAINING = '/st/',
  OPEN_TRAININGS = '/sft/',
  PRICES = '/sp/',
}

export const VenueOfferingToVenueTabMapping = {
  [VenueOfferingType.VENUE_OFFERING_CLASS_ACTIVITY]: VenueProfileTabsPrefix.CLASSES,
  [VenueOfferingType.VENUE_OFFERING_COURSE_ACTIVITY]: VenueProfileTabsPrefix.COURSES,
  [VenueOfferingType.VENUE_OFFERING_CAMP_ACTIVITY]: VenueProfileTabsPrefix.CAMPS,
  [VenueOfferingType.VENUE_OFFERING_EVENT_ACTIVITY]: VenueProfileTabsPrefix.EVENTS,
  [VenueOfferingType.VENUE_OFFERING_EDUCATION_ACTIVITY]: VenueProfileTabsPrefix.EDUCATION,
  [VenueOfferingType.VENUE_OFFERING_WORKSHOP_ACTIVITY]: VenueProfileTabsPrefix.WORKSHOPS,
  [VenueOfferingType.VENUE_OFFERING_RETREAT_ACTIVITY]: VenueProfileTabsPrefix.RETREATS,
  [VenueOfferingType.VENUE_OFFERING_TRAINING_ACTIVITY]: VenueProfileTabsPrefix.TRAINING,
  [VenueOfferingType.VENUE_OFFERING_OPENTRAINING_ACTIVITY]: VenueProfileTabsPrefix.OPEN_TRAININGS,
  [VenueOfferingType.VENUE_OFFERING_SHOP]: VenueProfileTabsPrefix.PRICES,
  [VenueOfferingType.VENUE_OFFERING_VIDEO]: VenueProfileTabsPrefix.OVERVIEW,
  [VenueOfferingType.VENUE_OFFERING_SPORTS_FIELDS]: VenueProfileTabsPrefix.SPORTS_FIELDS,
  [VenueOfferingType.VENUE_OFFERING_PUBLIC_MATCHES]: VenueProfileTabsPrefix.OVERVIEW,
  [VenueOfferingType.VENUE_OFFERING_TEAM]: VenueProfileTabsPrefix.OVERVIEW,
  // For the below offering types, we link to the Overview page (there's no standalone page for them)
  [VenueOfferingType.VENUE_OFFERING_LIVESTREAM]: VenueProfileTabsPrefix.OVERVIEW,
  [VenueOfferingType.VENUE_OFFERING_INDOOR_ACTIVITY]: VenueProfileTabsPrefix.OVERVIEW,
  [VenueOfferingType.VENUE_OFFERING_OUTDOOR_ACTIVITY]: VenueProfileTabsPrefix.OVERVIEW,
  [VenueOfferingType.VENUE_OFFERING_VOUCHER]: VenueProfileTabsPrefix.OVERVIEW,
  [VenueOfferingType.VENUE_OFFERING_RENTAL]: VenueProfileTabsPrefix.OVERVIEW,
}

export const CLASSNAME_CONTAINED_CONTENT = 'w-full max-w-7xl my-0 mx-auto'

export const THRESHOLD_TO_SHOW_VENUE_COUNT = 3

export const DISCOVER_OFFERING_PROPERTIES_MAPPING: {
  [key in DiscoverOfferingType]: {
    linkPrefix: DiscoverPrefix
    nameForTracking: string
    translationKey:
      | 'camps'
      | 'classes-and-trainings'
      | 'courses'
      | 'education'
      | 'events'
      | 'livestreams'
      | 'trainings'
      | 'public-matches'
      | 'retreats'
      | 'open-trainings'
      | 'videos'
      | 'workshops'
  }
} = {
  [DiscoverOfferingType.DISCOVER_OFFERING_CAMP_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.CAMPS,
    translationKey: 'camps',
    nameForTracking: 'camps',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_CLASS_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.CLASSES,
    translationKey: 'classes-and-trainings',
    nameForTracking: 'classes and trainings',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_COURSE_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.COURSES,
    translationKey: 'courses',
    nameForTracking: 'courses',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_EDUCATION_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.EDUCATION,
    translationKey: 'education',
    nameForTracking: 'education',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_EVENT_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.EVENTS,
    translationKey: 'events',
    nameForTracking: 'events',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_LIVESTREAM]: {
    linkPrefix: DiscoverPrefix.LIVESTREAMS,
    translationKey: 'livestreams',
    nameForTracking: 'livestreams',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_OPENTRAINING_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.OPEN_TRAININGS,
    translationKey: 'open-trainings',
    nameForTracking: 'open trainings',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_PRIVATE_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.PRIVATE,
    translationKey: 'events',
    nameForTracking: 'private',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_RENTAL_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.RENTAL,
    translationKey: 'events',
    nameForTracking: 'rental',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_PUBLIC_MATCH]: {
    linkPrefix: DiscoverPrefix.PUBLIC_MATCHES,
    translationKey: 'public-matches',
    nameForTracking: 'public matches',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_RETREAT_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.RETREATS,
    translationKey: 'retreats',
    nameForTracking: 'retreats',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_TRAINING_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.TRAININGS,
    translationKey: 'trainings',
    nameForTracking: 'trainings',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_VIDEO]: {
    linkPrefix: DiscoverPrefix.VIDEOS,
    translationKey: 'videos',
    nameForTracking: 'videos',
  },
  [DiscoverOfferingType.DISCOVER_OFFERING_WORKSHOP_ACTIVITY]: {
    linkPrefix: DiscoverPrefix.WORKSHOPS,
    translationKey: 'workshops',
    nameForTracking: 'workshops',
  },
}

export const DISCOVER_TAB_MAPPING_FOR_AMPLITUDE: {
  [key in DiscoverOfferingType]: string
} = {
  [DiscoverOfferingType.DISCOVER_OFFERING_CAMP_ACTIVITY]: 'camps',
  [DiscoverOfferingType.DISCOVER_OFFERING_CLASS_ACTIVITY]: 'classes and trainings',
  [DiscoverOfferingType.DISCOVER_OFFERING_COURSE_ACTIVITY]: 'courses',
  [DiscoverOfferingType.DISCOVER_OFFERING_EDUCATION_ACTIVITY]: 'education',
  [DiscoverOfferingType.DISCOVER_OFFERING_EVENT_ACTIVITY]: 'events',
  [DiscoverOfferingType.DISCOVER_OFFERING_LIVESTREAM]: 'livestreams',
  [DiscoverOfferingType.DISCOVER_OFFERING_OPENTRAINING_ACTIVITY]: 'open trainings',
  [DiscoverOfferingType.DISCOVER_OFFERING_PRIVATE_ACTIVITY]: 'private',
  [DiscoverOfferingType.DISCOVER_OFFERING_RENTAL_ACTIVITY]: 'rental',
  [DiscoverOfferingType.DISCOVER_OFFERING_PUBLIC_MATCH]: 'public matches',
  [DiscoverOfferingType.DISCOVER_OFFERING_RETREAT_ACTIVITY]: 'retreats',
  [DiscoverOfferingType.DISCOVER_OFFERING_TRAINING_ACTIVITY]: 'trainings',
  [DiscoverOfferingType.DISCOVER_OFFERING_VIDEO]: 'videos',
  [DiscoverOfferingType.DISCOVER_OFFERING_WORKSHOP_ACTIVITY]: 'workshops',
}

export const AMPLITUDE_EVENT_NAME = {
  SPOT_BOOKING_VIEWED_SPOTS_PAGE: 'Viewed Spots Page',
  SPOT_BOOKING_CLICKED_CONTINUE_TO_CHECKOUT: 'Clicked Continue to Checkout from Spots Preview',
  SPOT_BOOKING_CLICKED_MODIFY_SPOT: 'Clicked Modify Spot (Save)',
  PRODUCTS_VIEWED_MY_PRODUCTS_PAGE: 'Viewed My Products Page',
  PRODUCTS_VIEWED_MY_PRODUCT_DETAIL_PAGE: 'Viewed My Product Detail Page',
  PRODUCTS_VIEWED_PUBLIC_PRODUCT_PAGE: 'Viewed Public Product Page',
  REFER_A_FRIEND_VIEWED_HUB: 'Viewed Refer Hub',
  REFER_A_FRIEND_GENERATED_CODE: 'Generated Refer Code',
  REFER_A_FRIEND_CLICKED_SHARE: 'Clicked Share Refer Code',
  REFER_A_FRIEND_CLICKED_COPY: 'Clicked Copy Refer Code',
}

export const REFER_A_FRIEND_FROM = {
  MENU: 'menu',
  HUB: 'refer hub',
}

export const SPOT_BOOKING_FROM = {
  ACTIVITY_DETAIL: 'activity_detail',
}

export const EventTypeToVenueTabMapping: { [key in EventType]: VenueProfileTabsPrefix } = {
  [EventType.CLASS]: VenueProfileTabsPrefix.CLASSES,
  [EventType.COURSE]: VenueProfileTabsPrefix.COURSES,
  [EventType.CAMP]: VenueProfileTabsPrefix.CAMPS,
  [EventType.EVENT]: VenueProfileTabsPrefix.EVENTS,
  [EventType.EDUCATION]: VenueProfileTabsPrefix.EDUCATION,
  [EventType.OPENTRAINING]: VenueProfileTabsPrefix.OPEN_TRAININGS,
  [EventType.PRIVATE]: VenueProfileTabsPrefix.EVENTS,
  [EventType.RENTAL]: VenueProfileTabsPrefix.EVENTS,
  [EventType.RETREAT]: VenueProfileTabsPrefix.RETREATS,
  [EventType.TRAINING]: VenueProfileTabsPrefix.TRAINING,
  [EventType.WORKSHOP]: VenueProfileTabsPrefix.WORKSHOPS,
}

export enum AuthRoutes {
  LOGIN = '/auth/',
  REGISTER = '/auth/register',
  SIGNOUT = '/u/logout',
}
